"use client";

import {
  CopyButton,
  DataTable,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { WIDGET_H3 } from "@/components/tool-kit/styles";
import { useToolNumber } from "@/components/tools/tool-ui";
import type { UNCERTAINTY_PROPAGATION_CALCULATOR_STRINGS } from "@/components/tools/uncertainty-propagation-calculator";
import { t } from "@/lib/i18n/interpolate";
import { toSuperscript } from "@/lib/tool-kit/math/primes";
import {
  type RoundedMeasurement,
  roundMeasurement,
  type UncertaintyRounding,
} from "@/lib/tool-kit/math/uncertainty";

/*
 * The results of the uncertainty calculator: the rounded answer, the unrounded one,
 * each input's contribution, and the working lines the active mode produced.
 */

type Strings = typeof UNCERTAINTY_PROPAGATION_CALCULATOR_STRINGS;
type Format = (value: number, options?: Intl.NumberFormatOptions) => string;

export interface ContributionRow {
  label: string;
  /** The value and uncertainty as typed, so 1.000 keeps its zeros. */
  valueText: string;
  uncertaintyText: string;
  sensitivity: number;
  term: number;
  share: number;
  nonlinear: boolean;
}

export interface PropagationView {
  value: number;
  uncertainty: number;
  relative: number | null;
  contributions: ContributionRow[];
  working: string[];
}

/** A quantity in the reader's notation; scientific when very large or small. */
export function general(fmt: Format, value: number): string {
  const size = Math.abs(value);
  if (size !== 0 && (size < 1e-4 || size >= 1e9)) {
    return fmt(value, { notation: "scientific", maximumSignificantDigits: 6 });
  }
  return fmt(value, { maximumSignificantDigits: 6 });
}

/** "9.80 ± 0.11", or "(6.674 ± 0.002) × 10⁻¹¹" when a power of ten reads better. */
function formatRounded(rounded: RoundedMeasurement, fmt: Format): string {
  const magnitude = Math.abs(rounded.value) || Math.abs(rounded.uncertainty);
  const exponent = magnitude > 0 ? Math.floor(Math.log10(magnitude)) : 0;
  if (rounded.place >= -6 && exponent < 9) {
    const fixed = {
      minimumFractionDigits: rounded.decimals,
      maximumFractionDigits: rounded.decimals,
    };
    return `${fmt(rounded.value, fixed)} ± ${fmt(rounded.uncertainty, fixed)}`;
  }
  const scale = 10 ** exponent;
  const decimals = Math.max(0, exponent - rounded.place);
  const fixed = {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  };
  return `(${fmt(rounded.value / scale, fixed)} ± ${fmt(rounded.uncertainty / scale, fixed)}) × 10${toSuperscript(exponent)}`;
}

export function UncertaintyResults({
  view,
  rule,
  strings,
}: {
  view: PropagationView;
  rule: UncertaintyRounding;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const rounded = roundMeasurement(view.value, view.uncertainty, rule);
  const headline = rounded
    ? formatRounded(rounded, fmt)
    : `${general(fmt, view.value)} ± 0`;
  const unrounded = `${general(fmt, view.value)} ± ${general(fmt, view.uncertainty)}`;
  const nonlinear = view.contributions.filter((c) => c.nonlinear);
  return (
    <>
      <StatGrid>
        <StatCard emphasis label={strings.result} value={headline} />
        <StatCard
          label={strings.relative}
          value={
            view.relative === null
              ? strings.notApplicable
              : `${fmt(view.relative * 100, { maximumSignificantDigits: 3 })}%`
          }
        />
        <StatCard label={strings.unrounded} value={unrounded} />
      </StatGrid>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyResult}
        small
        text={headline}
      />
      <h3 className={WIDGET_H3}>{strings.contributionsHeading}</h3>
      <DataTable
        head={[
          strings.inputColumn,
          strings.valueColumn,
          strings.sensitivityColumn,
          strings.termColumn,
          strings.shareColumn,
        ]}
        rows={view.contributions.map((c) => [
          c.label,
          `${c.valueText} ± ${c.uncertaintyText}`,
          general(fmt, c.sensitivity),
          general(fmt, c.term),
          `${fmt(c.share * 100, { maximumFractionDigits: 1 })}%`,
        ])}
      />
      <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
      <div className="space-y-1 break-words rounded-lg border border-border bg-muted/30 p-4 font-mono text-muted-foreground text-sm leading-relaxed">
        {view.working.map((line) => (
          <p key={line}>{line}</p>
        ))}
        {rounded ? (
          <p className="font-sans">
            {t(rule === "two" ? strings.roundedTwo : strings.roundedOneOrTwo, {
              result: headline,
            })}
          </p>
        ) : null}
      </div>
      {nonlinear.map((c) => (
        <Notice key={c.label}>{t(strings.nonlinear, { name: c.label })}</Notice>
      ))}
      <p className="text-muted-foreground text-xs">{strings.assumptions}</p>
    </>
  );
}
